import tocbot from 'tocbot';
import i18n from '../../utils/i18n.js';

class BceToc {
	constructor() {
		this.instances = new WeakMap();
		this.autoInit = false;
		this._active = 0;
	}

	init(selector = '.bce_toc') {
		const containers = document.querySelectorAll(selector);
		containers.forEach((el, index) => {
			if (!this.instances.has(el)) {
				this.create(el, { index });
			}
		});
	}

	create(element, options = {}) {
		const dataAccordion = element.getAttribute('data-accordion');
		const { accordion = dataAccordion !== 'false' } = options;
		if (this.instances.has(element)) {
			return this.instances.get(element);
		}

		const tocCol = element.querySelector(':scope > .container > .row > .col-md-3');
		const contentCol = element.querySelector(':scope > .container > .row > .col-md-9');

		if (!tocCol || !contentCol) {
			console.warn('BCE TOC: Expected .col-md-3 (toc) and .col-md-9 (content) inside .bce_toc');
			return null;
		}

		// Ensure stable, unique IDs for scoping tocbot to this element
		const uid = this._uniqueId();
		const tocId = tocCol.id || `tocbot-container-${uid}`;
		const contentId = contentCol.id || `tocbot-content-${uid}`;
		if (!tocCol.id) tocCol.id = tocId;
		if (!contentCol.id) contentCol.id = contentId;

		// Generate IDs for headings if missing (h1-h6)
		this.makeIds(`#${contentId}`, 'h1, h2, h3, h4, h5, h6');

		// Derive offsets from CSS var if available; fallback to 120
		const headerOffset = this._getCssVarPx('--header-height', 120);

		// Helper to ensure headline and wrapper exist
		const ensureHeadline = () => {
			const tocListContainer = tocCol.querySelector('.toc-list');
			if (!tocListContainer) return;

			// Check if wrapper already exists
			let wrapper = tocCol.querySelector('.toc-sticky-wrapper');

			if (!wrapper) {
				// Create wrapper
				wrapper = document.createElement('div');
				wrapper.className = 'toc-sticky-wrapper';

				// Wrap the toc-list
				tocListContainer.parentNode.insertBefore(wrapper, tocListContainer);
				wrapper.appendChild(tocListContainer);
			}

			// Add headline if missing
			if (!wrapper.querySelector('.toc-headline')) {
				const headline = document.createElement('div');
				headline.className = 'toc-headline';
				headline.textContent = i18n.t('toc', 'headline');
				wrapper.insertBefore(headline, tocListContainer);
			}
		};

		// Helper bound to this instance
		const markImportantH2 = () => {
			const importantH2 = contentCol.querySelector('.bce_toc-important h2');
			// Clear previous marks within this TOC only
			tocCol.querySelectorAll('a.toc-link.important-h2').forEach(a => a.classList.remove('important-h2'));
			if (importantH2 && importantH2.id) {
				const link = tocCol.querySelector(`a.toc-link[href="#${importantH2.id}"]`);
				if (link) link.classList.add('important-h2');
			}
		};

		// Initialize tocbot with custom scroll callback
		try {
			tocbot.init({
				tocSelector: `#${tocId}`,
				contentSelector: `#${contentId}`,
				headingSelector: 'h2', // 'h2, h3',
				collapseDepth: accordion ? 0 : 6,
				scrollSmooth: true,
				scrollSmoothDuration: 100,
				includeHtml: true,
				orderedList: false,
				enableUrlHashUpdateOnScroll: true,
				hasInnerContainers: true,
				headingsOffset: headerOffset,
				scrollSmoothOffset: -headerOffset,
				// Simple fix: larger bottom threshold
				bottomModeThreshold: 200,
				onClick: (e) => {
					try {
						if (e && typeof e.preventDefault === 'function') e.preventDefault();
						const anchor = e?.target?.closest ? e.target.closest('a[href^="#"]') : e?.target;
						const href = anchor && anchor.getAttribute ? anchor.getAttribute('href') : null;
						const target = href ? document.querySelector(href) : null;

						// Reset active link within this toc only
						tocCol.querySelectorAll('.is-active-link').forEach(link => link.classList.remove('is-active-link'));
						if (anchor) anchor.classList.add('is-active-link');

						if (target) {
							// Prefer scroll-margin-top for sticky headers
							try {
								target.style.scrollMarginTop = `${headerOffset}px`;
								target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
							} catch (_) {
								const y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
								window.scrollTo({ top: y, behavior: 'smooth' });
							}
						}

						tocbot.refresh();
						ensureHeadline();
						markImportantH2();
					} catch (err) {
						// noop
					}
				}
			});
		} catch (err) {
			console.error('BCE TOC: Failed to initialize tocbot', err);
			return null;
		}

		// Initial refresh/mark
		tocbot.refresh();
		ensureHeadline();
		markImportantH2();

		// Add headline after DOM is ready
		setTimeout(() => {
			ensureHeadline();
		}, 0);

		// Keep mark on scroll
		const onScroll = () => markImportantH2();
		window.addEventListener('scroll', onScroll);

		// Mark page as having TOC to scope scroll-behavior CSS
		this._active += 1;
		if (this._active === 1) {
			document.documentElement.classList.add('has-bce-toc');
		}

		const instance = {
			element,
			tocCol,
			contentCol,
			destroy: () => {
				try { tocbot.destroy(); } catch (_) { }
				window.removeEventListener('scroll', onScroll);
				this.instances.delete(element);
				// Remove marker if last instance is gone
				this._active = Math.max(0, this._active - 1);
				if (this._active === 0) {
					document.documentElement.classList.remove('has-bce-toc');
				}
			}
		};

		this.instances.set(element, instance);
		return instance;
	}

	makeIds(contentSelector, headingSelector) {
		const headings = document.querySelectorAll(`${contentSelector} ${headingSelector}`);
		const headingMap = {};
		Array.prototype.forEach.call(headings, (heading) => {
			if (heading.id) return;
			let id = (heading.textContent || '').trim().toLowerCase()
				.split(' ').join('-')
				.replace(/[!@#$%^&*():]/ig, '')
				.replace(/\./ig, '');
			headingMap[id] = !isNaN(headingMap[id]) ? ++headingMap[id] : 0;
			heading.id = headingMap[id] ? `${id}-${headingMap[id]}` : id;
		});
	}

	_uniqueId() {
		return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
	}

	_getCssVarPx(varName, fallbackPx = 0) {
		try {
			const root = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
			const body = getComputedStyle(document.body).getPropertyValue(varName).trim();
			const val = root || body || '';
			const n = parseFloat(val);
			return Number.isFinite(n) ? n : fallbackPx;
		} catch (e) {
			return fallbackPx;
		}
	}

}

export default new BceToc();
